import { MenuOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { Checkbox, Input, Popconfirm } from "antd";
import { OB_DELETE_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { deleteData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import React, { useCallback, useRef, useState } from "react";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import { isArrayAndHasValue } from "../../../utils/functions";
import "./index.css";
import ManPowerTableData from "./manPowerTableData";

const SortableTableBody = SortableContainer(
  ({ children, isDragging, selectedCount }) => {
    return (
      <tbody
        className={`scrollable-table-container ${
          isDragging ? "is-dragging" : ""
        }`}
      >
        {isDragging && selectedCount > 0 && (
          <tr className="drag-indicator">
            <td
              colSpan="100%"
              style={{
                backgroundColor: selectedCount > 1 ? "#4caf50" : "#2196f3",
                color: "white",
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              Moving {selectedCount} {selectedCount === 1 ? "row" : "rows"}{" "}
              {selectedCount > 1 ? "together" : ""} (Drag in progress)
            </td>
          </tr>
        )}
        {children}
      </tbody>
    );
  },
);

const RowHandler = SortableHandle(() => (
  <MenuOutlined
    className="cursore-pointer"
    style={{ fontSize: "14px" }}
    onMouseDown={(e) => {
      // Stop browser from selecting table text when drag starts
      e.preventDefault();
      if (typeof window !== "undefined" && window.getSelection) {
        window.getSelection()?.removeAllRanges?.();
      }
    }}
  />
));

// Must be defined outside render — recreating SortableElement breaks DnD.
const SortableOperationItem = SortableElement(
  ({
    sectionItem,
    selectedKeys,
    onToggleSelect,
    isDragging,
    focusedInput,
    setFocusedInput,
    view,
    manpowerMode,
    onChangeSAMValue,
    onChangeValue,
    handleConfirmDelete,
  }) => {
    const isSelected = selectedKeys.includes(sectionItem?.id);
    const isDraggingSelected = isSelected && isDragging;
    const machineName =
      sectionItem?.ob_machine_type_info?.name?.toLowerCase?.() || "";
    const machine_sam =
      machineName !== "helper" &&
      machineName !== "iron" &&
      machineName !== "hp"
        ? sectionItem?.sam
        : null;
    const helper_sam =
      machineName === "helper" || machineName === "hp"
        ? sectionItem?.sam
        : null;
    const iron_sam =
      machineName === "iron" || machineName === "ir"
        ? sectionItem?.sam
        : null;

    const handleFocus = (inputId) => setFocusedInput(inputId);
    const handleBlur = () => setFocusedInput(null);

    return (
      <tr
        key={`table_parent_key_${sectionItem?.id}`}
        data-sam-id={sectionItem?.id}
        className={`${sectionItem?.isNewlyAdded ? "highlight-new-row" : ""} ${
          isSelected ? "row-selected" : ""
        } ${isDraggingSelected ? "drag-selected" : ""}`}
        style={{
          backgroundColor: isDraggingSelected
            ? "#e8f5e9"
            : isSelected
            ? "#e3f2fd"
            : "transparent",
          borderLeft: isDraggingSelected
            ? "3px solid #4caf50"
            : isSelected
            ? "3px solid #2196f3"
            : "1px solid black",
          opacity: isDraggingSelected ? 0.8 : 1,
        }}
      >
        <td className="t-body sticky">{sectionItem?.id}</td>
        <td className="t-body sticky">{sectionItem?.name}</td>
        <td className="t-body sticky">{sectionItem?.condition}</td>
        <td className="t-body sticky">
          {machine_sam ? sectionItem?.sam : ""}
        </td>
        <td className="t-body sticky">
          {helper_sam ? sectionItem?.sam : ""}
        </td>
        <td className="t-body sticky">{iron_sam ? sectionItem?.sam : ""}</td>

        <td className="t-body sticky">
          <Input
            size="small"
            autoFocus={focusedInput === `mc_${sectionItem?.id}`}
            value={
              machine_sam ? sectionItem?.updated_sam ?? sectionItem?.sam : ""
            }
            disabled={view || manpowerMode || !machine_sam}
            onChange={(e) =>
              onChangeSAMValue(e.target.value, sectionItem?.id, "updated_sam")
            }
            onFocus={() => handleFocus(`mc_${sectionItem?.id}`)}
            onBlur={handleBlur}
            style={{ width: "75px" }}
          />
        </td>
        <td className="t-body sticky">
          <Input
            size="small"
            autoFocus={focusedInput === `hp_${sectionItem?.id}`}
            value={
              helper_sam ? sectionItem?.updated_sam ?? sectionItem?.sam : ""
            }
            disabled={view || manpowerMode || !helper_sam}
            onChange={(e) =>
              onChangeSAMValue(e.target.value, sectionItem?.id, "updated_sam")
            }
            onFocus={() => handleFocus(`hp_${sectionItem?.id}`)}
            onBlur={handleBlur}
            style={{ width: "75px" }}
          />
        </td>
        <td className="t-body sticky">
          <Input
            size="small"
            autoFocus={focusedInput === `ir_${sectionItem?.id}`}
            value={
              iron_sam ? sectionItem?.updated_sam ?? sectionItem?.sam : ""
            }
            disabled={view || manpowerMode || !iron_sam}
            onChange={(e) =>
              onChangeSAMValue(e.target.value, sectionItem?.id, "updated_sam")
            }
            onFocus={() => handleFocus(`ir_${sectionItem?.id}`)}
            onBlur={handleBlur}
            style={{ width: "75px" }}
          />
        </td>

        <td className="t-body sticky">
          {sectionItem?.ob_machine_type_info?.name}
        </td>
        <td className="t-body sticky">
          {machine_sam
            ? Number(
                (60 / (sectionItem?.updated_sam || sectionItem?.sam)).toFixed(
                  2,
                ),
              )
            : ""}
        </td>
        <td className="t-body sticky">
          {helper_sam
            ? Number(
                (60 / (sectionItem?.updated_sam || sectionItem?.sam)).toFixed(
                  2,
                ),
              )
            : ""}
        </td>
        <td className="t-body sticky">
          {iron_sam
            ? Number(
                (60 / (sectionItem?.updated_sam || sectionItem?.sam)).toFixed(
                  2,
                ),
              )
            : ""}
        </td>

        {(view || manpowerMode) && (
          <>
            <td className="t-body sticky">
              {sectionItem?.worker_machine_sam
                ? Number(sectionItem?.worker_machine_sam).toFixed(2)
                : ""}
            </td>
            <td className="t-body sticky">
              {sectionItem?.worker_helper_sam
                ? Number(sectionItem?.worker_helper_sam).toFixed(2)
                : ""}
            </td>
            <td className="t-body sticky">
              {sectionItem?.worker_iron_sam
                ? Number(sectionItem?.worker_iron_sam).toFixed(2)
                : ""}
            </td>
          </>
        )}

        {manpowerMode && (
          <>
            <td className="t-body sticky">
              <Input
                size="small"
                autoFocus={focusedInput === `worker_mc_${sectionItem?.id}`}
                value={sectionItem?.manual_worker_machine_sam}
                disabled={!sectionItem?.worker_machine_sam}
                onChange={(e) =>
                  onChangeValue(
                    e.target.value,
                    sectionItem?.id,
                    "manual_worker_machine_sam",
                  )
                }
                onFocus={() => handleFocus(`worker_mc_${sectionItem?.id}`)}
                onBlur={handleBlur}
                style={{ width: "75px" }}
              />
            </td>
            <td className="t-body sticky">
              <Input
                size="small"
                autoFocus={focusedInput === `worker_hp_${sectionItem?.id}`}
                value={sectionItem?.manual_worker_helper_sam}
                disabled={!sectionItem?.worker_helper_sam}
                onChange={(e) =>
                  onChangeValue(
                    e.target.value,
                    sectionItem?.id,
                    "manual_worker_helper_sam",
                  )
                }
                onFocus={() => handleFocus(`worker_hp_${sectionItem?.id}`)}
                onBlur={handleBlur}
                style={{ width: "75px" }}
              />
            </td>
            <td className="t-body sticky">
              <Input
                size="small"
                autoFocus={focusedInput === `worker_ir_${sectionItem?.id}`}
                value={sectionItem?.manual_worker_iron_sam}
                disabled={!sectionItem?.worker_iron_sam}
                onChange={(e) =>
                  onChangeValue(
                    e.target.value,
                    sectionItem?.id,
                    "manual_worker_iron_sam",
                  )
                }
                onFocus={() => handleFocus(`worker_ir_${sectionItem?.id}`)}
                onBlur={handleBlur}
                style={{ width: "75px" }}
              />
            </td>
          </>
        )}

        <td className="t-body sticky">
          <Input
            size="small"
            autoFocus={focusedInput === `color_${sectionItem?.id}`}
            value={sectionItem?.color || ""}
            onChange={(e) =>
              onChangeValue(e.target.value, sectionItem?.id, "color")
            }
            onFocus={() => handleFocus(`color_${sectionItem?.id}`)}
            onBlur={handleBlur}
            style={{ width: "75px" }}
            placeholder="Color"
          />
        </td>

        {!view && !manpowerMode && (
          <td>
            <div className="action-container">
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect(sectionItem?.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <Popconfirm
                title="Are you sure you want to delete?"
                description={`Are you sure you want to delete ${sectionItem?.name}?`}
                onConfirm={() => handleConfirmDelete(sectionItem)}
                onCancel={() => {}}
                okText="Ok"
                cancelText="Cancel"
                disabled={false}
              >
                <MinusCircleOutlined
                  className="cursore-pointer"
                  style={{
                    fontSize: "14px",
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
              <RowHandler />
            </div>
          </td>
        )}
      </tr>
    );
  },
);

const TableData = (props) => {
  const {
    view,
    edit,
    manpowerMode,
    sectionId,
    sectionName,
    sectionItems,
    removeRow,
    onChangeValue,
    onChangeSAMValue,
    onSortTableBody,
  } = props;

  const [focusedInput, setFocusedInput] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;
  const sectionItemsRef = useRef(sectionItems);
  sectionItemsRef.current = sectionItems;

  const helperClass =
    selectedKeys.length > 1
      ? "helperContainerClass multiple-selected"
      : "helperContainerClass";

  const clearMultiDragPreview = useCallback(() => {
    document.querySelectorAll(".multi-drag-source-hidden").forEach((row) => {
      row.classList.remove("multi-drag-source-hidden");
    });
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      selectedKeys.forEach((id) => {
        const row = document.querySelector(`tr[data-sam-id="${id}"]`);
        if (row) {
          row.classList.add("drag-selected");
        }
      });
    } else {
      document.querySelectorAll(".drag-selected").forEach((row) => {
        row.classList.remove("drag-selected");
      });
      clearMultiDragPreview();
    }
  }, [isDragging, selectedKeys, clearMultiDragPreview]);

  const buildMultiRowDragHelper = useCallback((draggedIndex) => {
    const items = sectionItemsRef.current || [];
    const draggedItemId = items[draggedIndex]?.id;
    if (!draggedItemId) return;

    const currentSelected = selectedKeysRef.current || [];
    const movingIds = (
      currentSelected.includes(draggedItemId)
        ? currentSelected
        : [draggedItemId]
    );
    const movingInOrder = items
      .filter((r) => movingIds.includes(r.id))
      .map((r) => r.id);

    if (movingInOrder.length <= 1) return;

    // Helper is appended by react-sortable-hoc after onSortStart — defer DOM patch
    const paintHelper = () => {
      const helper =
        document.querySelector(".helperContainerClass.multiple-selected") ||
        document.querySelector(".helperContainerClass");
      if (!helper) return;

      const sourceWidth =
        document
          .querySelector(`tr[data-sam-id="${draggedItemId}"]`)
          ?.getBoundingClientRect?.().width ||
        helper.getBoundingClientRect().width;

      helper.classList.add("multi-drag-helper");
      helper.setAttribute("data-count", String(movingInOrder.length));
      helper.style.display = "block";
      helper.style.width = `${sourceWidth}px`;
      helper.style.height = "auto";
      helper.style.padding = "0";
      helper.style.overflow = "visible";
      helper.innerHTML = "";

      const previewTable = document.createElement("table");
      previewTable.className = "multi-drag-helper-table";
      previewTable.style.width = "100%";
      previewTable.style.borderCollapse = "collapse";
      previewTable.style.tableLayout = "fixed";
      previewTable.style.background = "#e8f5e9";
      previewTable.style.boxShadow = "0 6px 16px rgba(0,0,0,0.2)";
      previewTable.style.border = "2px solid #4caf50";

      const previewBody = document.createElement("tbody");
      movingInOrder.forEach((id) => {
        const sourceRow = document.querySelector(`tr[data-sam-id="${id}"]`);
        if (!sourceRow) return;
        const clone = sourceRow.cloneNode(true);
        clone.removeAttribute("data-sam-id");
        clone.classList.add("multi-drag-preview-row");
        clone.style.opacity = "1";
        clone.style.backgroundColor = "#e8f5e9";
        previewBody.appendChild(clone);

        // Dim original checked rows (picked up into the ghost)
        if (id !== draggedItemId) {
          sourceRow.classList.add("multi-drag-source-hidden");
        }
      });

      previewTable.appendChild(previewBody);
      helper.appendChild(previewTable);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(paintHelper);
    });
  }, []);

  const handleToggleSelect = useCallback((id, checked) => {
    setSelectedKeys((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((k) => k !== id);
    });
  }, []);

  const handleToggleSelectAll = useCallback(
    (checked) => {
      if (!isArrayAndHasValue(sectionItems)) {
        setSelectedKeys([]);
        return;
      }
      setSelectedKeys(checked ? sectionItems.map((item) => item.id) : []);
    },
    [sectionItems],
  );

  const allSelected =
    isArrayAndHasValue(sectionItems) &&
    sectionItems.every((item) => selectedKeys.includes(item.id));
  const someSelected =
    selectedKeys.length > 0 && !allSelected;

  const handleConfirmDelete = async (sectionItem) => {
    const { ob_detail_id, id } = sectionItem || {};
    if (ob_detail_id) {
      const response = await deleteData(
        `${OB_DELETE_ENDPOINT}/${ob_detail_id}`,
      );
      if (response?.code === 200) {
        removeRow(id);
        alertPop("success", response?.message || "Deleted Successfully!");
      }
    } else {
      removeRow(id);
    }
  };

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setTimeout(() => {
        setIsDragging(false);
      }, 50);
    }
  }, [isDragging]);

  React.useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseUp]);

  const onSortStartHandler = useCallback(
    ({ index }) => {
      // Prevent accidental text selection while dragging
      if (typeof window !== "undefined" && window.getSelection) {
        window.getSelection()?.removeAllRanges?.();
      }
      setIsDragging(true);
      buildMultiRowDragHelper(index);
    },
    [buildMultiRowDragHelper],
  );

  const onSortEndHandler = useCallback(
    ({ oldIndex, newIndex }) => {
      setIsDragging(false);
      clearMultiDragPreview();

      if (!sectionItems || !Array.isArray(sectionItems) || !onSortTableBody) {
        return;
      }

      if (
        oldIndex === newIndex ||
        oldIndex < 0 ||
        newIndex < 0 ||
        oldIndex >= sectionItems.length ||
        newIndex >= sectionItems.length
      ) {
        return;
      }

      try {
        const draggedItemId = sectionItems[oldIndex]?.id;
        if (!draggedItemId) return;

        const currentSelected = selectedKeysRef.current || [];
        const movingKeySet = new Set(
          currentSelected.includes(draggedItemId)
            ? currentSelected
            : [draggedItemId],
        );

        // Preserve original list order of selected rows
        const movingRows = sectionItems.filter((r) =>
          movingKeySet.has(r.id),
        );
        const remainingRows = sectionItems.filter(
          (r) => !movingKeySet.has(r.id),
        );

        // Map drop index into the remaining (non-selected) list
        let insertIndex = 0;
        if (oldIndex < newIndex) {
          for (let i = 0; i <= newIndex; i++) {
            if (!movingKeySet.has(sectionItems[i]?.id)) insertIndex++;
          }
        } else {
          for (let i = 0; i < newIndex; i++) {
            if (!movingKeySet.has(sectionItems[i]?.id)) insertIndex++;
          }
        }
        insertIndex = Math.max(
          0,
          Math.min(insertIndex, remainingRows.length),
        );

        const newData = [
          ...remainingRows.slice(0, insertIndex),
          ...movingRows,
          ...remainingRows.slice(insertIndex),
        ];

        onSortTableBody(newData, sectionId);
        setSelectedKeys(movingRows.map((r) => r.id));
      } catch (error) {
        console.error("Error in onSortEndHandler:", error);
      }
    },
    [sectionItems, onSortTableBody, sectionId, clearMultiDragPreview],
  );

  const getHelperDimensions = useCallback(({ node, index }) => {
    const rect = node.getBoundingClientRect();
    const items = sectionItemsRef.current || [];
    const draggedItemId = items[index]?.id;
    const currentSelected = selectedKeysRef.current || [];
    const count =
      draggedItemId && currentSelected.includes(draggedItemId)
        ? currentSelected.length
        : 1;

    return {
      width: rect.width,
      height: Math.max(rect.height, rect.height * count),
    };
  }, []);

  return (
    <SortableTableBody
      onSortStart={onSortStartHandler}
      onSortEnd={onSortEndHandler}
      isDragging={isDragging}
      selectedCount={
        selectedKeys.length > 0 ? selectedKeys.length : 0
      }
      axis="y"
      lockAxis="y"
      lockToContainerEdges={true}
      lockOffset={["30%", "50%"]}
      helperClass={helperClass}
      useDragHandle={true}
      getHelperDimensions={getHelperDimensions}
    >
      <tr
        key={`table_header_key_${sectionId}`}
        style={{ position: "sticky", top: 72, zIndex: 1 }}
      >
        <th className="bc-java text-center">NO.</th>
        <th className="bc-java text-center">{sectionName}</th>
        <th className="bc-java text-center">CONDITION</th>
        <th className="bc-java text-center">M/C</th>
        <th className="bc-java text-center">HP</th>
        <th className="bc-java text-center">IR</th>
        <th className="bc-java text-center">M/C</th>
        <th className="bc-java text-center">HP</th>
        <th className="bc-java text-center">IR</th>
        <th className="bc-java text-center">TYPE</th>
        <th className="bc-java text-center">M/C</th>
        <th className="bc-java text-center">HP</th>
        <th className="bc-java text-center">IR</th>
        {(view || manpowerMode) && (
          <>
            <th className="bc-java text-center">M/C</th>
            <th className="bc-java text-center">HP</th>
            <th className="bc-java text-center">IR</th>
          </>
        )}
        {manpowerMode && (
          <>
            <th className="bc-java text-center">M/C</th>
            <th className="bc-java text-center">HP</th>
            <th className="bc-java text-center">IR</th>
          </>
        )}
        <th className="bc-java text-center">Color</th>
        {!view && !manpowerMode && (
          <th className="bc-java text-center">
            <div className="action-container">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => handleToggleSelectAll(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <RowHandler />
            </div>
          </th>
        )}
      </tr>

      {selectedKeys.length > 0 && (
        <tr>
          <td
            colSpan={
              !view && !manpowerMode
                ? manpowerMode
                  ? 19
                  : view || manpowerMode
                  ? 16
                  : 13
                : manpowerMode
                ? 18
                : view || manpowerMode
                ? 15
                : 12
            }
            style={{
              backgroundColor: "#f3e5f5",
              padding: "8px",
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#6a1b9a",
            }}
          >
            {selectedKeys.length} row{selectedKeys.length > 1 ? "s" : ""}{" "}
            checked
            {selectedKeys.length > 1 &&
              " — drag any checked row's handle to move all together"}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedKeys([]);
              }}
              style={{
                marginLeft: "10px",
                padding: "2px 8px",
                fontSize: "10px",
                backgroundColor: "#9c27b0",
                color: "white",
                border: "none",
                borderRadius: "3px",
                cursor: "pointer",
              }}
            >
              Clear Selection
            </button>
          </td>
        </tr>
      )}

      {manpowerMode ? (
        <ManPowerTableData
          sectionItems={sectionItems}
          onChangeValue={onChangeValue}
          onChangeSAMValue={onChangeSAMValue}
          view={view}
          edit={edit}
          manpowerMode={manpowerMode}
          removeRow={removeRow}
        />
      ) : (
        isArrayAndHasValue(sectionItems) &&
        sectionItems.map((sectionItem, index) => (
          <SortableOperationItem
            key={`item-${sectionItem?.id ?? index}`}
            index={index}
            collection={sectionId}
            sectionItem={sectionItem}
            selectedKeys={selectedKeys}
            onToggleSelect={handleToggleSelect}
            isDragging={isDragging}
            focusedInput={focusedInput}
            setFocusedInput={setFocusedInput}
            view={view}
            manpowerMode={manpowerMode}
            onChangeSAMValue={onChangeSAMValue}
            onChangeValue={onChangeValue}
            handleConfirmDelete={handleConfirmDelete}
          />
        ))
      )}
    </SortableTableBody>
  );
};

export default React.memo(TableData);
